import React, { useEffect, useMemo, useState } from "react";
import {
  getMembersByUserType,
  getUserTypes,
} from "../../services/accessService";
import { getFluxxUserId } from "@/services/postLoginUser";

type CheckResult = {
  loading?: boolean;
  hasAccess?: boolean;
  fluxxId?: string;
  message?: string;
};

const UserEligibility: React.FC = () => {
  const [userTypes, setUserTypes] = useState<any[]>([]);
  const [selectedUserType, setSelectedUserType] = useState("");

  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [search, setSearch] = useState("");
  const [expandedCards, setExpandedCards] = useState<
  Record<string, boolean>
>({});

  const [results, setResults] = useState<
    Record<string, CheckResult>
  >({});

  useEffect(() => {
    const loadUserTypes = async () => {
      try {
        const response = await getUserTypes();
        setUserTypes(response || []);
      } catch (error) {
        console.error("Failed to load user types", error);
      }
    };

    loadUserTypes();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedUserType) {
        setMembers([]);
        return;
      }

      try {
        setLoadingMembers(true);

        const response = await getMembersByUserType(
          Number(selectedUserType)
        );

        setMembers(response || []);
        setResults({});
      } catch (error) {
        console.error("Failed to load members", error);
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    loadMembers();
  }, [selectedUserType]);

const handleCheck = async (member: any) => {
  const key = member.mail;

  setExpandedCards((prev) => ({
    ...prev,
    [key]: true,
  }));

  setResults((prev) => ({
    ...prev,
    [key]: {
      loading: true,
    },
  }));

try {

  const response = await getFluxxUserId(key);

  const fluxxId = response?.userId;

  if (fluxxId) {
    setResults((prev) => ({
      ...prev,
      [key]: {
        loading: false,
        hasAccess: true,
        fluxxId,
        message: `${member.displayName} has Fluxx access`,
      },
    }));
  } else {
    setResults((prev) => ({
      ...prev,
      [key]: {
        loading: false,
        hasAccess: false,
        message: `${member.displayName} does not have Fluxx access`,
      },
    }));
  }
} catch {
  setResults((prev) => ({
    ...prev,
    [key]: {
      loading: false,
      hasAccess: false,
      message: "Unable to verify Fluxx access. Please try again.",
    },
  }));
}
};

const handleClose = (upn: string) => {
  setExpandedCards((prev) => ({
    ...prev,
    [upn]: false,
  }));
};

  const filteredMembers = useMemo(() => {
    const term = search.toLowerCase();

    return members.filter(
      (member) =>
        member.displayName
          ?.toLowerCase()
          .includes(term) ||
        member.userPrincipalName
          ?.toLowerCase()
          .includes(term)
    );
  }, [members, search]);

  const MemberSkeleton = () => (
  <div className="border border-gray-200 rounded-lg bg-white p-4 animate-pulse">
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-48 mb-3"></div>
        <div className="h-3 bg-gray-200 rounded w-72"></div>
      </div>

      <div className="h-9 w-20 bg-gray-200 rounded"></div>
    </div>
  </div>
);

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-md font-semibold text-gray-800">
              User Eligibility
            </h2>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User Type
                </label>

                <select
                  value={selectedUserType}
                  onChange={(e) =>
                    setSelectedUserType(e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="">
                    Select User Type
                  </option>

                  {userTypes.map((type, index) => (
                    <option
                      key={`${type.id}-${index}`}
                      value={type.id}
                    >
                      {type.user_type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search User
                </label>

                <input
                  type="text"
                  placeholder="Search by name or email..."
                  disabled={!selectedUserType}
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
                />
              </div>
            </div>

           {loadingMembers && (
  <div className="space-y-4">
    {[...Array(6)].map((_, index) => (
      <MemberSkeleton key={`${index}-skeleton`} />
    ))}
  </div>
)}

{loadingMembers ? (
  <div className="space-y-4">
    {[...Array(6)].map((_, index) => (
      <MemberSkeleton key={`${index}-skeleton`} />
    ))}
  </div>
) : (
  <div className="space-y-4">
            {filteredMembers.map((member, index) => {
  const key = member.mail;

  const state = results[key];
  const isExpanded = expandedCards[key];

  return (
    <div
      key={`${member.id}-${index}`}
      className="border border-gray-200 rounded-lg bg-white overflow-hidden"
    >
      {isExpanded && state?.message && (
        <div
          className={`px-4 py-3 border-b text-sm ${
            state.hasAccess
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-red-50 text-red-700 border-red-200"
          }`}
        >
          {state.message}
        </div>
      )}

      <div className="p-4 flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="font-semibold text-gray-800">
            {member.displayName}
          </div>

          <div className="text-sm text-gray-500">
            {member.mail}
          </div>

          {isExpanded && state?.fluxxId && (
            <div className="mt-2 text-sm">
              <span className="font-medium">
                Fluxx ID:
              </span>{" "}
              <span className="text-gray-700">
                {state.fluxxId}
              </span>
            </div>
          )}
        </div>

        {!isExpanded ? (
          <button
            onClick={() => handleCheck(member)}
            disabled={state?.loading}
            className="bg-[var(--color-brand)] text-white px-4 py-2 rounded-md text-sm disabled:bg-gray-300"
          >
            {state?.loading
              ? "Checking..."
              : "Check"}
          </button>
        ) : (
          <button
            onClick={() => handleClose(key)}
            className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
})}
            </div>
)}

            {!loadingMembers &&
              filteredMembers.length === 0 &&
              selectedUserType && (
                <div className="text-center py-10 text-gray-500">
                  No users found
                </div>
              )}

           

            {!selectedUserType && (
              <div className="text-center text-gray-500 py-10">
                Select a User Type to view members
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEligibility;